<template>
  <div class="guide">
    <h1 class="guide__title">📖 操作指南</h1>
    <p class="guide__subtitle">新手必讀，快速了解如何使用店家後台</p>

    <!-- 重要提示 -->
    <div class="guide__highlight-bar">
      新增時段批次完成後，可至上方「預約連結」複製您的專屬預約網址，分享給客人開始預約
    </div>

    <!-- 大綱 -->
    <nav class="guide__toc">
      <p class="guide__toc-title">目錄</p>
      <ol class="guide__toc-list" start="0">
        <li>
          <a href="#step0" @click.prevent="scrollTo('step0')">0. 登入店家後台</a>
        </li>
        <li v-for="section in sections" :key="section.id">
          <a :href="`#${section.id}`" @click.prevent="scrollTo(section.id)">
            {{ section.label }}
          </a>
        </li>
        <li>
          <a href="#faq" @click.prevent="scrollTo('faq')">常見問題 Q&amp;A</a>
        </li>
      </ol>
    </nav>

    <!-- 步驟 0：登入後台 -->
    <section id="step0" class="guide__section">
      <h2 class="guide__section-title">
        <span class="guide__step-badge">0</span>
        登入店家後台
      </h2>
      <ul class="guide__list">
        <li>審核通過後，您會收到一封來自 JILI 的 Email，標題為「入駐申請已審核通過」</li>
        <li>信件中有一個「登入店家後台」的橘色按鈕，點擊即可直接進入後台</li>
        <li>若找不到按鈕，信件中也會附上一段純文字連結，可複製貼入瀏覽器開啟</li>
        <li class="guide__list-item--highlight">✅ 強烈建議：進入後台後，請將網址加入書籤，方便日後直接登入，不需每次翻找 Email</li>
        <li>登入後即可看到本操作指南，以及左側的設定選單</li>
        <li>如果找不到當初的 Email，可至登入頁點選「忘記登入連結」，輸入 Email 重新寄送</li>
      </ul>
      <div class="guide__tip">
        💡 每間店家的登入連結是專屬個人的，不要將連結分享給他人，以免他人操作您的後台。
      </div>
    </section>

    <!-- 步驟說明 -->
    <section
      v-for="section in sections"
      :key="section.id"
      :id="section.id"
      class="guide__section"
    >
      <h2 class="guide__section-title">
        <span class="guide__step-badge">{{ section.step }}</span>
        {{ section.title }}
      </h2>
      <ul class="guide__list">
        <li v-for="(item, i) in section.items" :key="i"
          :class="{ 'guide__list-item--highlight': item.highlight }">
          {{ item.text || item }}
        </li>
      </ul>
      <div v-if="section.tip" class="guide__tip">
        💡 {{ section.tip }}
      </div>
    </section>

    <!-- Q&A -->
    <section id="faq" class="guide__section">
      <h2 class="guide__section-title">
        <span class="guide__step-badge">Q&amp;A</span>
        常見問題
      </h2>
      <div class="guide__faq">
        <div
          v-for="(qa, i) in faqs"
          :key="i"
          class="guide__faq-item"
        >
          <button
            class="guide__faq-q"
            @click="toggleFaq(i)"
            :class="{ 'is-open': openFaq === i }"
          >
            <span class="guide__faq-num">Q{{ i + 1 }}</span>
            {{ qa.q }}
            <span class="guide__faq-arrow">{{ openFaq === i ? '▲' : '▼' }}</span>
          </button>
          <div v-show="openFaq === i" class="guide__faq-a">
            {{ qa.a }}
          </div>
        </div>
      </div>
    </section>

    <!-- 回到頂部 -->
    <div class="guide__back-top">
      <button @click="scrollTo('top')">↑ 回到頂部</button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const openFaq = ref(null)
function toggleFaq(i) {
  openFaq.value = openFaq.value === i ? null : i
}

function scrollTo(id) {
  if (id === 'top') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  const el = document.getElementById(id)
  if (el) {
    const offset = 80
    const top = el.getBoundingClientRect().top + window.scrollY - offset
    window.scrollTo({ top, behavior: 'smooth' })
  }
}

const sections = [
  {
    id: 'step1',
    step: '1',
    title: '填寫店家資料',
    label: '1. 填寫店家資料',
    items: [
      '至左側選單點選「店家資料」',
      '填寫店名、地址、電話、Email、LINE ID',
      '上傳封面圖片（建議比例 1:1，JPG 或 PNG 格式）',
      '填寫店家描述，讓客人更了解你的服務特色',
      '填寫完畢後點選「儲存」',
    ],
    tip: '店家資料填寫越完整，客人預約意願越高！封面圖片是客人對你的第一印象。',
  },
  {
    id: 'step2',
    step: '2',
    title: '設定營業時間',
    label: '2. 設定營業時間',
    items: [
      '至左側選單點選「營業時間」',
      '勾選每週營業的星期幾',
      '設定每天的開始時間與結束時間',
      '設定完畢後點選「儲存」',
    ],
    tip: '營業時間是讓客人了解您的上班時段，不代表可預約時間，實際可預約時段需在「服務管理」中另外建立。',
  },
  {
    id: 'step3',
    step: '3',
    title: '新增服務項目',
    label: '3. 新增服務項目',
    items: [
      '至左側選單點選「服務管理」',
      '點選「＋ 新增服務」',
      '填寫服務名稱、價格、服務時長（分鐘）',
      '儲存後即可建立預約時段',
    ],
    tip: '服務時長會影響客人可預約的時段，例如設定 90 分鐘，系統只會顯示足夠 90 分鐘的時段給客人選擇。',
  },
  {
    id: 'step4',
    step: '4',
    title: '建立可預約時段',
    label: '4. 建立可預約時段',
    items: [
      '在「服務管理」頁面，找到要建立時段的服務',
      '點選「＋ 新增時段」',
      '選擇日期、開始時間，系統會自動計算結束時間',
      '可批次建立多個時段，方便一次設定一整週',
      '若有時間衝突的時段，系統會自動跳過，其餘時段正常建立',
      '新增時段批次完成後，可至上方「預約連結」複製您的專屬預約網址，分享給客人開始預約',
    ],
    tip: '建議每個月定期批次建立下個月的時段，讓客人能提早安排預約，避免臨時找不到合適時間。',
  },
  {
    id: 'step5',
    step: '5',
    title: '管理客人預約',
    label: '5. 管理客人預約',
    items: [
      '至左側選單點選「預約管理」',
      '可依日期、狀態篩選預約記錄',
      '點選預約可查看客人姓名、電話、Email 等資訊',
      '可確認或取消客人的預約',
      '取消後時段會自動釋出，其他客人可再預約',
    ],
    tip: '客人完成預約後，系統會自動發送 Email 通知到您的店家信箱，請確認店家資料中的 Email 填寫正確。',
  },
  {
    id: 'step6',
    step: '6',
    title: '查詢時段狀況',
    label: '6. 查詢時段狀況',
    items: [
      '至左側選單點選「時段管理」',
      '點選日期可查看該天所有時段的狀態',
      '可一鍵清除整天的時段（若需要臨時休假）',
      '時段管理為檢視用途，新增時段請至「服務管理」',
    ],
    tip: '若某天需要臨時休假，可至「時段管理」選擇該日期後一鍵刪除所有時段。',
  },
]

const faqs = [
  {
    q: '客人在前台看不到我的店？',
    a: '店家狀態需為「上線」才會顯示給客人。請確認店家資料已填寫完整，並聯絡平台管理員審核上線。',
  },
  {
    q: '時段建立了，但客人說看不到可預約的時間？',
    a: '請到「服務管理」確認有新增服務項目，並在該服務下建立時段。時段必須是「開放」狀態才能被預約。另外，若服務時長為 90 分鐘，但該時段剩餘時間不足 90 分鐘，系統會自動隱藏，避免客人預約到無法完成的時段。',
  },
  {
    q: '怎麼修改或刪除已建立的時段？',
    a: '時段建立後無法單獨編輯，若需調整請先刪除該時段再重新建立。如需刪除整天的時段，可至「時段管理」選擇日期後一鍵清除。',
  },
  {
    q: '客人完成預約後，我會收到通知嗎？',
    a: '系統會發送 Email 通知到您的店家信箱，請確認店家資料中的 Email 填寫正確。',
  },
  {
    q: '客人預約後想取消，怎麼處理？',
    a: '客人可自行至預約頁面取消預約，取消後該時段會自動釋出，可再重新預約。請注意：預約時間 48 小時以上可自由取消；48 小時以內則無法自行取消，若有特殊狀況請直接聯絡店家處理。',
  },
  {
    q: '找不到當初的登入 Email 怎麼辦？',
    a: '請至登入頁面，點選「重新寄送登入連結」，輸入您的店家 Email，系統會將登入連結重新寄到信箱。若仍有問題，請聯絡平台管理員。',
  },
  {
    q: '忘記後台登入密碼怎麼辦？',
    a: '請至登入頁面點選「忘記密碼」，輸入您的店家 Email，系統會寄送重設密碼連結到信箱，依指示重新設定即可。',
  },
  {
    q: '營業時間設定了，為什麼還要手動建立時段？',
    a: '營業時間是讓客人了解您的上班時段，實際可預約的時間需另外在「服務管理」中手動建立時段，這樣可以更彈性地控制每天的預約數量。',
  },
  {
    q: '我想暫停接受預約，要怎麼做？',
    a: '可以到「時段管理」將特定日期的時段全部刪除，客人就無法預約該日。若要長期暫停，請聯絡平台管理員將店家暫時下架。',
  },
  {
    q: '客人說收不到預約確認信？',
    a: '請提醒客人檢查垃圾郵件資料夾。若仍未收到，可能是客人填寫的 Email 有誤，可至「預約管理」查看該筆預約的聯絡資訊確認。',
  },
  {
    q: 'LINE ID 填了，客人可以直接聯絡我嗎？',
    a: '是的，客人在前台店家頁面可以看到「LINE 聯絡」按鈕，點擊後會直接開啟 LINE 加入好友。請確認填寫的是您的 LINE ID（非電話號碼）。',
  },
]
</script>

<style scoped>
.guide {
  max-width: 760px;
  margin: 0 auto;
}

/* 標題 */
.guide__title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
}
.guide__subtitle {
  color: #7f8c8d;
  font-size: 0.95rem;
  margin-bottom: 28px;
}

/* 平台公告 */
.guide__ann {
  background: #fff8e1; border: 1px solid #ffe082; border-left: 4px solid #f59e0b;
  border-radius: 8px; padding: 12px 16px; margin-bottom: 12px;
}
.guide__ann__summary {
  display: flex; align-items: center; justify-content: space-between;
  cursor: pointer; list-style: none; font-weight: 700; color: #333; font-size: 0.95rem;
}
.guide__ann__summary::-webkit-details-marker { display: none; }
.guide__ann__summary::after { content: ' ▼'; font-size: 0.7rem; color: #aaa; }
details[open] .guide__ann__summary::after { content: ' ▲'; }
.guide__ann__content { font-size: 0.88rem; color: #555; margin: 10px 0 0; white-space: pre-wrap; line-height: 1.7; }

/* 重要提示列 */
.guide__highlight-bar {
  background: #f0faf4;
  border: 1px solid #a8ddb8;
  border-left: 4px solid #2d7a4f;
  border-radius: 6px;
  padding: 12px 16px;
  color: #2d7a4f;
  font-weight: 600;
  font-size: 0.93rem;
  margin-bottom: 20px;
}

/* 大綱 */
.guide__toc {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-left: 4px solid #8b6f5c;
  border-radius: 8px;
  padding: 18px 24px;
  margin-bottom: 36px;
}
.guide__toc-title {
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 10px;
  font-size: 0.95rem;
}
.guide__toc-list {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.guide__toc-list li a {
  color: #8b6f5c;
  text-decoration: none;
  font-size: 0.92rem;
  transition: color 0.15s;
}
.guide__toc-list li a:hover {
  color: #5a4438;
  text-decoration: underline;
}

/* 段落 */
.guide__section {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  padding: 24px 28px;
  margin-bottom: 24px;
  scroll-margin-top: 80px;
}
.guide__section-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.guide__step-badge {
  background: #8b6f5c;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

/* 步驟列表 */
.guide__list {
  margin: 0 0 0 4px;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #444;
  font-size: 0.93rem;
  line-height: 1.6;
}

/* 凸顯項目 */
.guide__list-item--highlight {
  color: #2d7a4f;
  font-weight: 600;
  background: #f0faf4;
  border: 1px solid #a8ddb8;
  border-radius: 5px;
  padding: 6px 10px;
  list-style: none;
  margin-left: -20px;
}

/* 提示 */
.guide__tip {
  margin-top: 16px;
  background: #fffbf0;
  border: 1px solid #f0d98c;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 0.88rem;
  color: #7a6200;
  line-height: 1.6;
}

/* Q&A */
.guide__faq {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.guide__faq-item {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}
.guide__faq-q {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: #fafafa;
  border: none;
  cursor: pointer;
  font-size: 0.92rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: left;
  transition: background 0.15s;
}
.guide__faq-q:hover { background: #f0f0f0; }
.guide__faq-q.is-open { background: #f3ede9; color: #8b6f5c; }
.guide__faq-num {
  background: #8b6f5c;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}
.guide__faq-arrow {
  margin-left: auto;
  font-size: 0.75rem;
  color: #aaa;
  flex-shrink: 0;
}
.guide__faq-a {
  padding: 14px 16px 14px 44px;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.7;
  background: #fff;
  border-top: 1px solid #eee;
}

/* 回到頂部 */
.guide__back-top {
  text-align: center;
  margin: 32px 0 16px;
}
.guide__back-top button {
  background: none;
  border: 1px solid #ccc;
  color: #888;
  font-size: 0.88rem;
  padding: 8px 20px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;
}
.guide__back-top button:hover {
  background: #f0f0f0;
  color: #555;
}

/* 手機版 */
@media (max-width: 768px) {
  .guide__section { padding: 18px 16px; }
  .guide__toc { padding: 14px 16px; }
  .guide__faq-a { padding-left: 16px; }
}
</style>
